import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export const REVIEWS_URL = 'https://review.codepulsehq.com';
export const ANALYTICS_URL = 'https://codepulsehq.com';

type Product = 'analytics' | 'reviews';

interface ProductSwitcherProps {
  /** Which product the current site represents. Used to label the trigger and mark the active menu item. */
  current?: Product;
  /** Match the LandingHeader scroll-state pattern so the trigger blends into transparent and solid headers. */
  useDarkText?: boolean;
  /** Extra classes to merge onto the trigger (e.g. margin). */
  className?: string;
}

export default function ProductSwitcher({
  current = 'analytics',
  useDarkText = true,
  className = '',
}: ProductSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const triggerLabel = current === 'reviews' ? 'Reviews' : 'Analytics';
  const triggerDotColor = current === 'reviews' ? '#f97316' : '#2dd4bf';
  const triggerDotHalo =
    current === 'reviews' ? '0 0 0 3px rgba(249, 115, 22, 0.18)' : '0 0 0 3px rgba(20, 184, 166, 0.18)';

  const triggerClasses = [
    'inline-flex items-center gap-2 pl-3 pr-2.5 py-1.5 rounded-full text-sm font-semibold transition-colors',
    'border',
    useDarkText
      ? 'text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-gray-800/60'
      : 'text-white border-white/30 hover:bg-white/10',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Switch product"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className={triggerClasses}
      >
        <span
          aria-hidden="true"
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: triggerDotColor, boxShadow: triggerDotHalo }}
        />
        {triggerLabel}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="CodePulse products"
          className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[60] overflow-hidden"
        >
          <a
            role="menuitem"
            href={current === 'analytics' ? '/' : ANALYTICS_URL}
            aria-current={current === 'analytics' ? 'page' : undefined}
            onClick={() => setIsOpen(false)}
            className="flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <span
              aria-hidden="true"
              className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: '#2dd4bf' }}
            />
            <span className="flex flex-col">
              <strong className="text-sm font-semibold text-gray-900 dark:text-white">CodePulse Analytics</strong>
              <small className="text-xs text-gray-500 dark:text-gray-400">DORA metrics &amp; team health</small>
            </span>
          </a>
          <a
            role="menuitem"
            href={current === 'reviews' ? '/' : REVIEWS_URL}
            aria-current={current === 'reviews' ? 'page' : undefined}
            target={current === 'reviews' ? undefined : '_blank'}
            rel={current === 'reviews' ? undefined : 'noopener noreferrer'}
            onClick={() => setIsOpen(false)}
            className="flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <span
              aria-hidden="true"
              className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: '#f97316' }}
            />
            <span className="flex flex-col">
              <strong className="text-sm font-semibold text-gray-900 dark:text-white">CodePulse Reviews</strong>
              <small className="text-xs text-gray-500 dark:text-gray-400">AI pull-request reviews in Slack</small>
            </span>
          </a>
        </div>
      )}
    </div>
  );
}
